<script setup lang="ts">
import autoAnimate from '@formkit/auto-animate';
import { computed, onMounted, ref } from 'vue';
import { useCartSummary } from '../../composables/useCartSummary';
import * as CheckoutHelpers from '../../utils/checkoutHelpers';
import { formatCurrency } from '../../utils/currencyHelpers';
import { formatWithoutRounding } from '../../utils/productPrice';
import FeedbackMessage from '../shoppingCart/FeedbackMessage.vue';
import { useShoppingCartStore } from '../shoppingCart/stores/shoppingCartStore';

const props = defineProps<{
  headingClass?: string;
  showCheckoutLink?: boolean;
  checkoutAvailable?: boolean;
  error?: string;
}>();

const root = ref();
const totalList = ref();
const shoppingCartStore = useShoppingCartStore();
const { shippingCostFormatted, totalAmount } = useCartSummary();

const error = computed(() => {
  return props.error;
});

/**
 * Computes whether the shopping cart is eligible for free shipping.
 *
 * @returns {boolean} True if the shopping cart is eligible for free shipping, false otherwise.
 */
const freeShipping = computed(() => {
  if (!shoppingCartStore.cart) return false;

  return CheckoutHelpers.qualifiesForFreeShipping(
    shoppingCartStore.cart?.freeShippingActive,
    shoppingCartStore.cart?.freeShippingLimit,
    shoppingCartStore.cart?.subTotal
  );
});

onMounted(() => {
  autoAnimate(root.value);
  autoAnimate(totalList.value);
});
</script>

<template>
  <div ref="root" class="checkout-summary checkout-summary--checkout">
    <h2 class="checkout-summary__heading sr-only">{{ $t('summary') }}</h2>
    <FeedbackMessage :message="error" type="error" />
    <div>
      <div ref="totalList" class="checkout-summary__group checkout-summary__group--checkout">
        <div
          v-if="shoppingCartStore.cart?.discountTotal?.amount"
          class="checkout-summary__entry checkout-summary__entry"
        >
          <div>{{ $t('discountTotal') }}</div>
          <div>
            <span class="checkout-summary__discount"
              >-
              {{
                shoppingCartStore.cart.discountTotal &&
                formatCurrency(shoppingCartStore.cart.discountTotal.currency)
              }}
              {{ formatWithoutRounding(shoppingCartStore.cart.discountTotal.amount) }}</span
            >
          </div>
        </div>
        <div
          v-if="shoppingCartStore.cart?.total?.amount"
          class="checkout-summary__entry checkout-summary__entry"
        >
          <div>{{ $t('shipping') }}</div>
          <div>
            <span>{{ shippingCostFormatted }}</span>
          </div>
        </div>
        <div
          v-if="shoppingCartStore.cart && !freeShipping"
          class="checkout-summary__entry checkout-summary__entry--gray"
        >
          <div class="checkout-summary__shipping-tip">
            {{ shoppingCartStore.shippingCallToActionText }}
          </div>
        </div>
      </div>
      <h4 class="checkout-summary__total">
        <span>{{ $t('total') }}: </span>
        <span
          >{{
            shoppingCartStore.cart?.total && formatCurrency(shoppingCartStore.cart.total.currency)
          }}
          {{ shoppingCartStore.cart?.total && formatWithoutRounding(totalAmount) }}</span
        >
      </h4>
    </div>
  </div>
</template>
